"use client";
import { useDispatch } from "react-redux";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import useCandidateDashboard from "@/modules/candidate/hooks/useCandidateDashboard";
import apiClient from "@/shared/services/api/apiClient";
import { logoutThunk } from "@/modules/auth/store/authThunk";
import { useState, useRef, useEffect } from "react";
import {
  Menu,
  X,
  LayoutDashboard,
  BriefcaseBusiness,
  User,
  Bell,
  Settings,
  LogOut,
  ChevronDown,
  Building2,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";

const MENU_ITEMS = [
  {
    title: "Dashboard",
    href: "/candidate",
    icon: LayoutDashboard,
  },
  {
    title: "My Applications",
    href: "/candidate/applications",
    icon: BriefcaseBusiness,
  },
];

export default function CandidateLayout() {
  const { pathname } = useLocation();
  const { dashboard, loading, error, refresh } = useCandidateDashboard();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  const [user, setUser] = useState({
    name: "Candidate",
    email: "",
    role: "Applicant",
    profile: "",
  });

  // Keep the navbar and child pages in sync with the actual candidate dashboard data.
  useEffect(() => {
    const candidate = dashboard?.candidate;
    if (!candidate) return;

    setUser((previous) => ({
      ...previous,
      name: candidate.name || previous.name,
      email: candidate.email || previous.email,
      role: candidate.role || previous.role,
    }));
  }, [dashboard]);

  // Fetch Logged-In Candidate Info
  useEffect(() => {
    try {
      const rawUser =
        localStorage.getItem("user") ||
        sessionStorage.getItem("user") ||
        localStorage.getItem("userData") ||
        sessionStorage.getItem("userData");

      if (rawUser) {
        const parsed = JSON.parse(rawUser);
        const userData = parsed.user || parsed;

        setUser({
          name:
            userData.name ||
            userData.fullName ||
            userData.username ||
            userData.email?.split("@")[0] ||
            "Candidate",
          role: userData.role || userData.designation || "Applicant",
          email: userData.email || userData.user?.email || "",
          profile: userData.profile || userData.avatar || userData.image || "",
        });
      }
    } catch (error) {
      console.error("Error reading candidate user data:", error);
    }
  }, []);

  // Close profile dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getInitials = (name: string) => {
    if (!name) return "C";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const handleLogout = async () => {
    try {
      console.log("Candidate logout started");

      const response = await apiClient.post(
        "/candidate-auth/logout",
        {},
        {
          showLoader: false,
        }
      );

      console.log(
        "Candidate logout response:",
        response.data
      );
    } catch (error) {
      console.error(
        "Candidate logout API error:",
        error
      );
    } finally {
      // Access token + user data remove
      localStorage.removeItem("token");
      localStorage.removeItem("auth_user");
      localStorage.removeItem("user");
      localStorage.removeItem("userData");

      sessionStorage.removeItem("token");
      sessionStorage.removeItem("auth_user");
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("userData");

      // Redirect
      window.location.href = "/";
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-xs lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 z-50 h-screen w-64 border-r border-slate-200 bg-white transition-transform duration-300 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          }`}
      >
        {/* Logo Section */}
        <div className="flex h-[72px] items-center justify-between border-b border-slate-100 px-6">
          <div>
            <h1 className="text-xl font-bold text-blue-700">Dharohar</h1>
            <p className="text-xs font-medium text-slate-500">
              Candidate Portal
            </p>
          </div>

          <button
            type="button"
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="mt-4 space-y-1.5 px-3">
          {MENU_ITEMS.map((item) => {
            const Icon = item.icon;
            const active =
              item.href === "/candidate"
                ? pathname === "/candidate"
                : pathname.startsWith(item.href);

            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex h-10 items-center gap-3 rounded-xl px-3.5 text-sm font-semibold transition-all ${active
                    ? "bg-blue-50 text-blue-600"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
              >
                <Icon size={18} />
                {item.title}
              </Link>
            );
          })}
        </nav>

        {/* Sidebar Logout Button */}
        <div className="absolute bottom-5 w-full px-3">
          <button
            type="button"
            onClick={handleLogout}
            className="flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-red-200 text-xs font-semibold text-red-600 transition hover:bg-red-50 active:scale-95"
          >
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Layout Container */}
      <div className="lg:ml-64">
        {/* Navbar Header */}
        <header className="sticky top-0 z-30 h-[72px] border-b border-slate-200 bg-white/90 backdrop-blur-md">
          <div className="flex h-full items-center justify-between px-4 sm:px-6">
            {/* Mobile Menu Trigger */}
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                className="rounded-lg p-1.5 text-slate-600 hover:bg-slate-100 lg:hidden"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open menu"
              >
                <Menu size={20} />
              </button>

              <div className="min-w-0">
                <h2 className="truncate text-sm font-bold text-slate-900 sm:text-base">
                  {user.name}
                </h2>
                {user.email && (
                  <p className="max-w-[190px] truncate text-[11px] text-slate-500 sm:max-w-xs sm:text-xs">
                    {user.email}
                  </p>
                )}
              </div>
            </div>

            {/* Header Right Actions */}
            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2 text-xs sm:h-9 sm:px-3 sm:text-sm"
                asChild
              >
                <Link to="/">
                  <Building2 className="mr-2 h-4 w-4" />
                  Career Page
                </Link>
              </Button>

              <Link
                to="/candidate/notifications"
                className="relative rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 sm:rounded-xl sm:p-2"
              >
                <Bell className="h-[18px] w-[18px] sm:h-5 sm:w-5" />
                <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white" />
              </Link>

              {/* Profile Menu Dropdown */}
              <div className="relative" ref={profileMenuRef}>
                <button
                  type="button"
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="flex items-center gap-1.5 rounded-lg p-1 transition hover:bg-slate-100 sm:gap-2.5 sm:rounded-xl sm:p-1.5"
                >
                  {user.profile ? (
                    <img
                      src={user.profile}
                      alt={user.name}
                      className="flex items-center gap-1.5 rounded-lg p-1 transition hover:bg-slate-100 sm:gap-2.5 sm:rounded-xl sm:p-1.5"
                    />
                  ) : (
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white shadow-2xs">
                      {getInitials(user.name)}
                    </div>
                  )}

                  <div className="hidden text-left sm:block">
                    <h3 className="text-xs font-bold leading-tight text-slate-900 sm:text-sm">
                      {user.name}
                    </h3>
                    <p className="text-[11px] font-medium text-slate-500">
                      {user.role}
                    </p>
                  </div>

                  <ChevronDown
                    size={15}
                    className={`text-slate-400 transition-transform duration-200 ${profileDropdownOpen ? "rotate-180" : ""
                      }`}
                  />
                </button>

                {profileDropdownOpen && (
                  <div className="absolute right-0 z-50 mt-2 w-48 origin-top-right rounded-2xl border border-slate-200 bg-white p-2 shadow-xl ring-1 ring-black/5">
                    <div className="border-b border-slate-100 px-3 py-2 sm:hidden">
                      <p className="truncate text-xs font-bold text-slate-900">
                        {user.name}
                      </p>
                      <p className="truncate text-[11px] font-medium text-slate-500">
                        {user.role}
                      </p>
                    </div>

                    <div className="space-y-0.5 py-1">
                      <Link
                        to="/candidate/profile"
                        onClick={() => setProfileDropdownOpen(false)}
                        className="flex items-center gap-2.5 rounded-xl px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                      >
                        <User size={15} className="text-slate-400" />
                        My Profile
                      </Link>

                      <Link
                        to="/candidate/settings"
                        onClick={() => setProfileDropdownOpen(false)}
                        className="flex items-center gap-2.5 rounded-xl px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                      >
                        <Settings size={15} className="text-slate-400" />
                        Account Settings
                      </Link>
                    </div>

                    <div className="border-t border-slate-100 pt-1">
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-50"
                      >
                        <LogOut size={15} />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Dynamic Page Content */}
        <main className="h-[calc(100vh-72px)] overflow-y-auto p-4 sm:p-6">
          <Outlet context={{ dashboard, loading, error, refresh }} />
        </main>
      </div>
    </div>
  );
}